import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Alert, Button, Card, message, Skeleton, Table, Typography } from "antd"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { ContainerFull } from "../../components"
import { deleteInvoicesForOrders, getOrders } from "../../endpoints"

const { Title, Text } = Typography

interface Warning {
    invoiceId: number
    unselectedOrderIds: number[]
    message: string
}

export function DeleteInvoice(): JSX.Element {
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient()
    const [isDeleting, setIsDeleting] = useState(false)
    const [warnings, setWarnings] = useState<Warning[]>([])
    const [showWarnings, setShowWarnings] = useState(false)

    // Get orderIds from navigation state
    const orderIds = (location.state?.orderIds || []).map((id: any) => Number(id))

    // Fetch order details for the selected orders
    const { data: ordersData, isPending: ordersLoading } = useQuery({
        queryKey: ["getorders", "list", orderIds],
        queryFn: () => getOrders({ type: "list", orderIds }),
        enabled: orderIds.length > 0,
    })

    useEffect(() => {
        if (orderIds.length === 0) {
            message.error("No orders selected")
            navigate("/admin/orders")
        }
    }, [orderIds, navigate])

    const orders = ordersData?.orders || []
    const ordersWithInvoices = orders.filter(order => order.invoiceId)

    const handleDelete = async () => {
        setIsDeleting(true)
        try {
            const result = await deleteInvoicesForOrders({ orderIds })
            
            if (result.code === 1000) {
                message.success(result.message)
                // Refresh the orders data
                queryClient.invalidateQueries({ queryKey: ["getorders"] })
                // Navigate back to orders page
                navigate("/admin/orders")
            } else if (result.code === 400 && result.warnings) {
                setWarnings(result.warnings)
                setShowWarnings(true)
            } else {
                message.error(result.message || 'Failed to delete invoices')
            }
        } catch (error) {
            console.error('Error deleting invoices:', error)
            message.error('Failed to delete invoices')
        } finally {
            setIsDeleting(false)
        }
    }

    const handleCancel = () => {
        navigate("/admin/orders")
    }

    if (ordersLoading) {
        return (
            <ContainerFull>
                <Skeleton active />
            </ContainerFull>
        )
    }

    if (orderIds.length === 0) {
        return (
            <ContainerFull>
                <Alert
                    message="No Orders Selected"
                    description="Please go back and select orders to delete invoices for."
                    type="warning"
                    showIcon
                />
                <Button onClick={handleCancel} style={{ marginTop: 16 }}>
                    Back to Orders
                </Button>
            </ContainerFull>
        )
    }

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Title level={2}>Delete Invoices</Title>
            
            {showWarnings ? (
                <div>
                    <Alert
                        message="Cannot Delete Invoices"
                        description="Some invoices contain orders that are not selected. You must either select all orders from these invoices or deselect orders that belong to invoices with other orders."
                        type="error"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Card title="Conflicting Invoices" style={{ marginBottom: 24 }}>
                        {warnings.map((warning, index) => (
                            <div key={index} style={{ marginBottom: 16, padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                                <Text strong>Invoice {warning.invoiceId}</Text>
                                <div style={{ marginTop: 8 }}>
                                    <Text>Contains unselected orders: {warning.unselectedOrderIds.join(', ')}</Text>
                                </div>
                                <div style={{ marginTop: 4, fontSize: '0.9em', color: '#666' }}>
                                    {warning.message}
                                </div>
                            </div>
                        ))}
                    </Card>
                    
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Button onClick={handleCancel}>
                            Back to Orders
                        </Button>
                        <Button onClick={() => setShowWarnings(false)}>
                            Try Again
                        </Button>
                    </div>
                </div>
            ) : (
                <div>
                    <Alert
                        message="Delete Invoice Confirmation"
                        description={`You are about to delete invoices for ${orderIds.length} selected order(s). This action cannot be undone.`}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Card title="Selected Orders" style={{ marginBottom: 24 }}>
                        <Table
                            dataSource={orders}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: "Order ID",
                                    dataIndex: "id",
                                    key: "id",
                                },
                                {
                                    title: "Order Name",
                                    dataIndex: "ordername",
                                    key: "ordername",
                                },
                                {
                                    title: "Customer",
                                    key: "customer",
                                    render: (_, record) => 
                                        `${record.user.firstName} ${record.user.lastName}`,
                                },
                                {
                                    title: "Invoice ID",
                                    dataIndex: "invoiceId",
                                    key: "invoiceId",
                                    render: (invoiceId) => invoiceId || "No Invoice",
                                },
                                {
                                    title: "Total Price",
                                    key: "price",
                                    render: (_, record) => 
                                        record.totalPrice?.unit === "USD" 
                                            ? `$${record.totalPrice.price}`
                                            : `${record.totalPrice?.price || ""} ${record.totalPrice?.unit || ""}`,
                                },
                            ]}
                        />
                    </Card>

                    {ordersWithInvoices.length === 0 ? (
                        <Alert
                            message="No Invoices to Delete"
                            description="None of the selected orders have invoices associated with them."
                            type="info"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                    ) : (
                        <Alert
                            message={`${ordersWithInvoices.length} order(s) have invoices that will be deleted`}
                            type="info"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                    )}

                    <div style={{ display: 'flex', gap: 12 }}>
                        <Button onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            danger 
                            onClick={handleDelete}
                            loading={isDeleting}
                            disabled={ordersWithInvoices.length === 0}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Invoices'}
                        </Button>
                    </div>
                </div>
            )}
        </ContainerFull>
    )
}
